/** @format */

import React, { Fragment, useState } from "react";
import { FadeLoader } from "react-spinners";
import FellowLayout from "../../Fellow";
import { useSelector } from "react-redux";
import { profileSelector } from "store/reducers/ProfileReducer";
import EmptyNotice from "component/ErrorNotice";
import { useGetAllFellowSurveysQuery, useMarkFellowSurveyFilledMutation } from "store/services/adminAPI";
import { ReactComponent as HeaderIcon } from "images/svg/surveys-banner.svg";
import Select from "react-select";
import { ReactComponent as SearchIcon } from "images/svg/search.svg";
import { Col } from "react-bootstrap";
import { selectStyles } from "./selectStyles";
import { ReactComponent as AllSurveysIcon } from "images/svg/allSurveys.svg";
import { ReactComponent as UnfilledSurveysIcon } from "images/svg/unfilledSurveys.svg";
import { ReactComponent as FilledSurveysIcon } from "images/svg/filledSurveys.svg";
import { ReactComponent as SurveyLinkIcon } from "images/svg/surveylinkicon.svg";
import { ReactComponent as DisabledSurveyLinkIcon } from "images/svg/disabledSurveyIcon.svg";
import { ReactComponent as FilterIcon } from "images/svg/funnel.svg";

import "./styles.scss";
import { truncateString } from "utils/strings";
import SurveyImage from "images/3MTT/surveys-image.png";
import { formatDateWithoutTime } from "utils/date";
import FilterDropdown from "./components/FilterDropdown";


const FellowSurveys = () => {
  const { isLoading } = useSelector(profileSelector);

  const FilterOptions = [
    { value: "desc", label: "Newest to Oldest" },
    { value: "asc", label: "Oldest to Newest" },
  ];

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSort, setSort] = useState(FilterOptions[0]);
  const [activeTab, setActiveTab] = useState("all");
  const [filter, setFilter] = useState(`page=1`);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [isFilterVisible, setIsFilterVisible] = useState(false);

  const { data: surveys, isLoading: loadingSurveys, isFetching: fetchingSurveys } = useGetAllFellowSurveysQuery(filter);
  const [markSurveyFilled] = useMarkFellowSurveyFilledMutation();

  const handleSearch = () => {
    setCurrentPage(1);
    setFilter(`search=${searchTerm}&page=1`);
  };

  const handleSort = (selectedOption) => {
    setSort(selectedOption);
    setFilter(`sort=${selectedOption.value}&page=${currentPage}`);
  };

  const handleTabClick = (tabId) => {
    setCurrentPage(1);
    setActiveTab(tabId);
    if (tabId === "all") {
      setFilter(`page=1`);
    } else if (tabId === "unfilled") {
      setFilter(`status=unfilled&page=1`);
    } else if (tabId === "filled") {
      setFilter(`status=filled&page=1`);
    }
  };

  const handleEntriesPerPageChange = (e) => {
    const value = Number(e.target.value);
    setEntriesPerPage(value);
    setCurrentPage(1);
    setFilter(`page=1&page_size=${value}`);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    let newFilter = `page=${page}`;
    if (searchTerm) newFilter += `&search=${searchTerm}`;
    if (selectedSort) newFilter += `&sort=${selectedSort.value}`;
    if (activeTab !== "all") newFilter += `&status=${activeTab}`;
    if (entriesPerPage !== 10) newFilter += `&page_size=${entriesPerPage}`;
    setFilter(newFilter);
  };

  const renderPaginationButtons = () => {
    if (!surveys?.data?.total_pages) return null;
    
    const totalPages = surveys.data.total_pages;
    const buttons = [];
    
    buttons.push(
      <button
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="pagination-button"
      >
        &laquo;
      </button>
    );
    
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisibleButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxVisibleButtons - 1);
    
    if (endPage - startPage + 1 < maxVisibleButtons) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }
    
    if (startPage > 1) {
      buttons.push(
        <button key={1} onClick={() => handlePageChange(1)} className="pagination-button">
          1
        </button>
      );
      if (startPage > 2) {
        buttons.push(<span key="start-ellipsis">...</span>);
      }
    }
    
    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`pagination-button ${currentPage === i ? "active" : ""}`}
        >
          {i}
        </button>
      );
    }
    
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(<span key="end-ellipsis">...</span>);
      }
      buttons.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className="pagination-button"
        >
          {totalPages}
        </button>
      );
    }
    
    buttons.push(
      <button
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="pagination-button"
      >
        &raquo;
      </button>
    );
    
    return buttons;
  };

  const tabs = [
    {
      id: "all",
      title: "All",
      icon: <AllSurveysIcon className="tab-icon" />,
    },
    {
      id: "unfilled",
      title: "Not Filled",
      icon: <UnfilledSurveysIcon className="tab-icon" />,
    },
    {
      id: "filled",
      title: "Filled",
      icon: <FilledSurveysIcon className="tab-icon" />,
    },
  ];

  const SurveyCard = ({ item }) => {
    return (
      <div className="survey-card-col">
        <div className="survey-card">
          <div className="survery-card-top-row">
            <h4>{truncateString(item?.name, 20)}</h4>
            <span
              className={`survey-priority-tag ${
                item?.priority === "high"
                  ? "priority-high"
                  : item?.priority === "medium"
                  ? "priority-medium"
                  : item?.priority === "low"
                  ? "priority-low"
                  : "priority-high"
              }`}
            >
              {item?.priority || "High"}
            </span>
          </div>
          <div className="survey-image-container">
            <img src={SurveyImage} alt="survey-img" className="survey-card-image" />
          </div>
          <div className="survery-card-text">{item?.description}</div>
          <hr className="survey-card-divider" />
          <div className="survery-card-top-row">
            {item?.is_filled && (
              <div>
                <p>You've filled this</p>
              </div>
            )}

                <div>
              <p>Closes</p>
              <p><strong>{item?.due_date ? formatDateWithoutTime(item?.due_date): "--"}</strong></p>
            </div>
        
            
            {item?.is_filled || item?.due_date > Date.now() ? (
              <div>
                <DisabledSurveyLinkIcon className="survey-link-icon" />
              </div>
            ) : (
              <div>
                <a
                  href={item?.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => markSurveyFilled(item?.id)}
                >
                  <SurveyLinkIcon className="survey-link-icon" />
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Fragment>
      <FellowLayout>
        <FilterDropdown 
          isVisible={isFilterVisible}
          setIsVisible={setIsFilterVisible}
          setFilter={setFilter}
        />
        <div className="support-banner">
          <div className="welcome-left">
            <div className="welcome-text">
              <h4>Welcome to Surveys</h4>
              <h6>We get better when you tell us how well we currently serve you</h6>
            </div>
          </div>
          <div className="">
            <HeaderIcon />
          </div>
        </div>
        <Col xl={12}>
          <section className="custom-tab-1">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div className="support-tabs-container">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    className={`tab-button ${activeTab === tab.id ? "active" : ""}`}
                    onClick={() => handleTabClick(tab.id)}
                  >
                    {tab.icon}
                    <span className="ml-2">{tab.title}</span>
                  </button>
                ))}
              </div>

              <div className="filter-controls">
                <Select
                    value={selectedSort}
                    onChange={handleSort}
                    options={FilterOptions}
                    styles={selectStyles}
                    components={{
                      IndicatorSeparator: () => null,
                    }}
                  />
                  <FilterIcon onClick={()=>setIsFilterVisible(!isFilterVisible)} />

                </div>
              
            </div>
            <div className="support-search-wrapper survey-search">
              <div className="support-search-icon">
                <SearchIcon />
              </div>
              <input
                type="text"
                className="support-search-input"
                placeholder="Search for Articles"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
              />
            </div>

            <div className="tab-content">
              {tabs.find((tab) => tab.id === activeTab)?.component}
            </div>
          </section>
        </Col>
        {isLoading || loadingSurveys || fetchingSurveys ? (
          <div className="d-flex justify-content-center align-items-center height-15 bg-white ">
            <FadeLoader color="#36d7b7" />
          </div>
        ) : surveys?.data?.results?.length > 0 ? (
          <>
            <div className="survey-cards-container">
              {surveys.data?.results.map((survey, index) => (
                <SurveyCard key={survey.id || index} item={survey} />
              ))}
            </div>
            <div className="support-pagination-container">
              <div className="entries-selector">
                <span>Show</span>
                <select value={entriesPerPage} onChange={handleEntriesPerPageChange}>
                  <option value={3}>3</option>
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                </select>
                <span>Entries</span>
              </div>

              <div className="pagination-buttons">
                {renderPaginationButtons()}
              </div>
            </div>
          </>
        ) : (
          <>
            <EmptyNotice text="No surveys available yet" />
          </>
        )}
      </FellowLayout>
    </Fragment>
  );
};

export default FellowSurveys;